import { useEffect, useState } from "react";
import { Plus, Pencil, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import api, { formatApiError } from "../../lib/api";

const EMPTY_FORM = { name: "", email: "", password: "", role: "member", tiktok_username: "", tiktok_url: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchUsers = () => {
    api.get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(fetchUsers, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email, password: "", role: u.role, tiktok_username: u.tiktok_username || "", tiktok_url: u.tiktok_url || "" });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.email.trim()) {
      toast.error("E-posta alanı zorunludur");
      return;
    }
    if (!editing && !form.password) {
      toast.error("Şifre alanı zorunludur");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role, tiktok_username: form.tiktok_username, tiktok_url: form.tiktok_url };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, payload);
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/admin/users", form);
        toast.success("Hesap oluşturuldu");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/users/${deleteId}`);
      toast.success("Kullanıcı silindi");
      setUsers((prev) => prev.filter((u) => u.id !== deleteId));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-700 text-xl text-white">Kullanıcılar</h1>
        <button onClick={openCreate} data-testid="user-add-button" className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full">
          <Plus size={15} /> Hesap Oluştur
        </button>
      </div>
      <p className="text-sm text-[#64748B] mb-8">Admin ve üye hesaplarını oluşturun ve düzenleyin</p>

      <div className="rounded-2xl border border-white/[0.06] bg-[#0A0E17] overflow-x-auto">
        <table className="w-full min-w-[640px] text-left" data-testid="users-table">
          <thead>
            <tr className="border-b border-white/[0.06]">
              {["Ad", "E-posta", "Rol", "Oluşturulma", "İşlemler"].map((h) => (
                <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {loading ? (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-[#64748B]">Yükleniyor...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-[#64748B]" data-testid="users-empty">Kullanıcı bulunamadı.</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-4 text-sm font-semibold text-white">{u.name || "—"}</td>
                  <td className="px-5 py-4 text-xs text-[#A0ABC0]">{u.email}</td>
                  <td className="px-5 py-4">
                    <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-1 ${
                      u.role === "admin"
                        ? "text-cyan-300 bg-cyan-400/10 border-cyan-400/25"
                        : "text-[#A0ABC0] bg-white/5 border-white/10"
                    }`}>
                      {u.role === "admin" ? "Admin" : "Üye"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-[#A0ABC0]">{u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "—"}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(u)}
                        data-testid={`user-edit-${u.id}`}
                        aria-label="Düzenle"
                        className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteId(u.id)}
                        data-testid={`user-delete-${u.id}`}
                        aria-label="Sil"
                        className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-red-300 hover:border-red-400/40 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0E17] border-white/10 text-white max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">{editing ? "Kullanıcıyı Düzenle" : "Hesap Oluştur"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="user-form">
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Ad Soyad</label>
              <input data-testid="user-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Kullanıcının adı" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">E-posta *</label>
              <input data-testid="user-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ornek@mail.com" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">TikTok Kullanıcı Adı</label>
                <input data-testid="user-tiktok-username-input" className={inputCls} value={form.tiktok_username} onChange={(e) => setForm({ ...form, tiktok_username: e.target.value })} placeholder="@kullaniciadi" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">TikTok Profil Bağlantısı</label>
                <input data-testid="user-tiktok-url-input" className={inputCls} value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} placeholder="https://www.tiktok.com/@..." />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">
                {editing ? "Yeni Şifre (boş bırakırsanız değişmez)" : "Şifre *"}
              </label>
              <div className="relative">
                <input
                  data-testid="user-password-input"
                  type={showPassword ? "text" : "password"}
                  className={`${inputCls} pr-11`}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="En az 6 karakter"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="user-password-toggle"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-cyan-300"
                  aria-label="Şifreyi göster"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Rol</label>
              <select
                data-testid="user-role-select"
                className={`${inputCls} cursor-pointer`}
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="member">Üye (member)</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button type="submit" disabled={saving} data-testid="user-save-button" className="btn-glow w-full text-sm font-semibold text-white py-3.5 rounded-xl disabled:opacity-50">
              {saving ? "Kaydediliyor..." : editing ? "Değişiklikleri Kaydet" : "Hesabı Oluştur"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0A0E17] border-white/10 text-white" data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0ABC0]">Bu kullanıcıyı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel" className="bg-transparent border-white/10 text-white hover:bg-white/5">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" onClick={remove} className="bg-red-500 hover:bg-red-600 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
